import { useAuth0 } from '@auth0/auth0-react';
import { tasksApis } from '@gleamer/apis';
import { useSystem } from '@ohif/core';
import { useQuery } from '@tanstack/react-query';

const { fetchMyTasks } = tasksApis;

/**
 * How many of the user's tasks are read at once: all of them in practice, so the home and the task list can
 * count, search, filter and sort them without going back to viewer-api (its pages go up to 2,000).
 */
export const MY_TASKS_LIMIT = 1000;

/** The tasks assigned to the signed-in user, newest first. */
export function useMyTasks() {
  const { getAccessTokenSilently } = useAuth0();
  // Core singleton (single ui-next provider owns it) rather than the @ohif/ui
  // `useUserAuthentication()` api — see the note in useSaveLabelPass.
  const { servicesManager } = useSystem();
  const { userAuthenticationService } = servicesManager.services;

  return useQuery({
    queryKey: ['my-tasks'],
    queryFn: async () => {
      // Best-effort token refresh. The request's auth header comes from
      // UserAuthenticationService, so a failed OR hung silent-auth (e.g. no live
      // Auth0 session in e2e/smoke, where auth0-spa-js's lock can deadlock in
      // headless Chrome) must not block the task-list fetch. Race it against a
      // short timeout and proceed regardless.
      try {
        await Promise.race([
          getAccessTokenSilently(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('token refresh timeout')), 1500)
          ),
        ]);
      } catch {
        // proceed with the current token
      }
      return fetchMyTasks(
        userAuthenticationService,
        0,
        [{ id: 'createdAt', desc: true }],
        MY_TASKS_LIMIT
      );
    },
    refetchOnWindowFocus: true,
  });
}
